/**
 * @file Data Portability Composable - Export/Import functionality for RxDB collections
 * Provides JSON export/import with metadata, conflict resolution, and error handling
 * @since 2025-07-09
 */

import { useDb } from '../../core/db.js';
import { getCollection } from '../../collections/composables/collection.js';
import { getConfig } from '../../configs/composables/config.js';

/**
 * Data portability composable for exporting and importing RxDB collections
 * @returns {object} Export/import methods and reactive state
 */
export function useDataPortability() {
  const isExporting = Vue.ref(false);
  const isImporting = Vue.ref(false);
  const lastOperationResult = Vue.ref(null);

  /**
   * Downloads a JSON file in the browser
   * @param {object} data - Data to export
   * @param {string} filename - Name of the file to download
   */
  function downloadJsonFile(data, filename) {
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  }

  /**
   * Validates export format structure
   * @param {object} data - Export data to validate
   * @throws {Error} If validation fails
   */
  function validateExportFormat(data) {
    const requiredFields = [
      'collection',
      'version',
      'exportMetadata',
      'schema',
      'data',
    ];

    for (const field of requiredFields) {
      if (!data[field]) {
        throw new Error(`Missing required field: ${field}`);
      }
    }

    if (!Array.isArray(data.data)) {
      throw new Error('Data field must be an array');
    }

    if (data.version !== '1.0') {
      throw new Error(`Unsupported format version: ${data.version}`);
    }
  }

  /**
   * Validates database export format structure
   * @param {object} data - Database export data to validate
   * @throws {Error} If validation fails
   */
  function validateDatabaseExportFormat(data) {
    const requiredFields = [
      'database',
      'version',
      'exportMetadata',
      'collections',
    ];

    for (const field of requiredFields) {
      if (!data[field]) {
        throw new Error(`Missing required field: ${field}`);
      }
    }

    if (typeof data.collections !== 'object') {
      throw new Error('Collections field must be an object');
    }

    if (data.version !== '1.0') {
      throw new Error(`Unsupported format version: ${data.version}`);
    }
  }

  /**
   * Applies replace strategy - clears collection and inserts new data
   * @param {RxCollection} collection - Target collection
   * @param {Array} documents - Documents to insert
   * @returns {Promise<object>} Import results
   */
  async function applyReplaceStrategy(collection, documents) {
    const results = { imported: [], failed: [], errors: [] };

    try {
      // Clear existing data
      const existingDocs = await collection.find().exec();
      const primaryKey = collection.schema.jsonSchema.primaryKey;

      // Bulk remove using RxDB's bulkRemove
      if (existingDocs.length > 0) {
        const idsToRemove = existingDocs.map(doc => doc[primaryKey]);
        await collection.bulkRemove(idsToRemove);
      }

      // Bulk insert new documents
      const insertResult = await collection.bulkInsert(documents);

      results.imported = insertResult.success.map(doc => doc[primaryKey]);

      insertResult.error.forEach(err => {
        const docId = err.documentId || 'unknown';
        results.failed.push(docId);
        results.errors.push({
          id: docId,
          message: err.message,
          code: 'INSERT_ERROR',
        });
      });
    } catch (error) {
      console.error('Replace strategy error:', error);
      throw new Error(`Replace strategy failed: ${error.message}`);
    }

    return results;
  }

  /**
   * Applies upsert strategy - updates existing, inserts new
   * @param {RxCollection} collection - Target collection
   * @param {Array} documents - Documents to upsert
   * @returns {Promise<object>} Import results
   */
  async function applyUpsertStrategy(collection, documents) {
    const results = { imported: [], failed: [], errors: [] };
    const primaryKey = collection.schema.jsonSchema.primaryKey;

    try {
      const upsertResult = await collection.bulkUpsert(documents);

      results.imported = documents.map(doc => doc[primaryKey]);

      if (upsertResult.error) {
        upsertResult.error.forEach(err => {
          const docId = err.documentId || 'unknown';
          results.failed.push(docId);
          results.errors.push({
            id: docId,
            message: err.message,
            code: 'UPSERT_ERROR',
          });
        });
      }
    } catch (error) {
      console.error('Upsert strategy error:', error);
      throw new Error(`Upsert strategy failed: ${error.message}`);
    }

    return results;
  }

  /**
   * Exports a collection to JSON format
   * @param {string} collectionName - Name of the collection to export
   * @param {object} options - Export options
   * @param {string} options.filename - Optional filename for download
   * @returns {Promise<object>} Export result with metadata
   */
  async function exportCollection(collectionName, options = {}) {
    const { filename } = options;

    console.log('exportCollection called with:', { collectionName, options });
    isExporting.value = true;

    try {
      const db = useDb();
      console.log('Got db instance:', !!db);

      await db.ensureInitialized();
      console.log('Database initialized');

      // Check if db is still active
      if (!db.isInitialized) {
        throw new Error('Database is not properly initialized');
      }
      console.log('Database initialized:', db.isInitialized);

      const collectionWrapper = getCollection(collectionName);
      const collection = await collectionWrapper.getInstance();
      console.log(
        'Got collection:',
        !!collection,
        'closed:',
        collection?.closed
      );

      // Check if collection is still active
      if (collection.closed) {
        throw new Error(`Collection ${collectionName} is closed`);
      }

      // Get all documents
      const documents = await collection.find().exec();
      console.log('Got documents:', documents.length);

      // Prepare export data
      const exportData = {
        collection: collectionName,
        version: '1.0',
        exportMetadata: {
          exportedAt: new Date().toISOString(),
          exportedBy: 'system',
          recordCount: documents.length,
          airliftVersion: '2.0.0',
        },
        schema: collection.schema.jsonSchema,
        data: documents.map(doc => doc.toJSON()),
      };

      // Download file if filename provided
      if (filename) {
        downloadJsonFile(exportData, filename);
      }

      const result = {
        success: true,
        recordCount: documents.length,
        exportData: filename ? null : exportData,
      };

      lastOperationResult.value = result;
      return result;
    } catch (error) {
      const result = {
        success: false,
        error: error.message,
        code: 'EXPORT_ERROR',
      };

      lastOperationResult.value = result;
      throw error;
    } finally {
      isExporting.value = false;
    }
  }

  /**
   * Imports data into a collection
   * @param {string} collectionName - Target collection name
   * @param {object} data - Import data
   * @param {object} options - Import options
   * @param {string} options.strategy - Conflict resolution strategy
   * @returns {Promise<object>} Import results
   */
  async function importCollection(collectionName, data, options = {}) {
    const { strategy = 'upsert' } = options;

    data = Vue.toRaw(data);

    isImporting.value = true;

    try {
      // Validate format
      validateExportFormat(data);

      if (data.collection !== collectionName) {
        throw new Error(
          `Collection mismatch: expected ${collectionName}, got ${data.collection}`
        );
      }

      const { useDb } = await import('../../core/db.js');
      const db = useDb();

      await db.ensureInitialized();

      // Check if db is still active
      if (!db.isInitialized) {
        throw new Error('Database is not properly initialized');
      }

      const collectionWrapper = getCollection(collectionName);
      const collection = await collectionWrapper.getInstance();

      // Check if collection is still active
      if (collection.closed) {
        throw new Error(`Collection ${collectionName} is closed`);
      }

      // Apply strategy
      let results;
      switch (strategy) {
        case 'replace':
          results = await applyReplaceStrategy(collection, data.data);
          break;
        case 'upsert':
          results = await applyUpsertStrategy(collection, data.data);
          break;
        default:
          throw new Error(`Unknown strategy: ${strategy}`);
      }

      // Add summary
      results.summary = {
        total: data.data.length,
        successful: results.imported.length,
        failed: results.failed.length,
      };

      lastOperationResult.value = results;
      return results;
    } catch (error) {
      const result = {
        success: false,
        error: error.message,
        code: 'VALIDATION_ERROR',
      };

      lastOperationResult.value = result;
      throw error;
    } finally {
      isImporting.value = false;
    }
  }

  /**
   * Exports a config to JSON format
   * @param {string} configName - Name of the config to export
   * @param {object} options - Export options
   * @param {string} options.filename - Optional filename for download
   * @returns {Promise<object>} Export result with metadata
   */
  async function exportConfig(configName, options = {}) {
    const { filename } = options;
    isExporting.value = true;

    try {
      const config = getConfig(configName);
      const exportData = {
        config: configName,
        version: '1.0',
        exportMetadata: {
          exportedAt: new Date().toISOString(),
          airliftVersion: '2.0.0',
        },
        schema: config.schema,
        data: config.value,
      };

      if (filename) downloadJsonFile(exportData, filename);

      const result = {
        success: true,
        exportData: filename ? null : exportData,
      };
      lastOperationResult.value = result;
      return result;
    } catch (error) {
      lastOperationResult.value = {
        success: false,
        error: error.message,
        code: 'EXPORT_ERROR',
      };
      throw error;
    } finally {
      isExporting.value = false;
    }
  }

  /**
   * Imports data into a config
   * @param {string} configName - Target config name
   * @param {object} data - Import data
   * @returns {Promise<object>} Import results
   */
  async function importConfig(configName, data) {
    data = Vue.toRaw(data);
    isImporting.value = true;

    try {
      if (!data.config) throw new Error('Missing required field: config');
      if (data.data === undefined)
        throw new Error('Missing required field: data');
      if (data.config !== configName)
        throw new Error(
          `Config mismatch: expected ${configName}, got ${data.config}`
        );

      const config = getConfig(configName);
      await config.replace(data.data);

      const result = {
        success: true,
        summary: { config: configName, imported: true },
      };
      lastOperationResult.value = result;
      return result;
    } catch (error) {
      lastOperationResult.value = {
        success: false,
        error: error.message,
        code: 'VALIDATION_ERROR',
      };
      throw error;
    } finally {
      isImporting.value = false;
    }
  }

  /**
   * Exports entire database
   * @param {object} options - Export options
   * @param {string} options.filename - Optional filename for download
   * @param {Array} options.collections - Specific collections to export
   * @param {Array} options.configs - Specific configs to export
   * @returns {Promise<object>} Export result
   */
  async function exportDatabase(options = {}) {
    const {
      filename,
      collections: targetCollections,
      configs: targetConfigs,
    } = options;

    isExporting.value = true;

    try {
      if (!targetCollections?.length) {
        throw new Error(
          'targetCollections must be provided for database export'
        );
      }

      const exportedCollections = {};
      let totalRecords = 0;

      for (const collectionName of targetCollections) {
        try {
          const result = await exportCollection(collectionName);
          exportedCollections[collectionName] = result.exportData;
          totalRecords += result.recordCount;
        } catch (error) {
          console.warn(`Failed to export collection ${collectionName}:`, error);
        }
      }

      const exportedConfigs = {};
      const configsToExport = (targetConfigs || []).filter(
        name => name !== '_user'
      );

      for (const configName of configsToExport) {
        try {
          const result = await exportConfig(configName);
          exportedConfigs[configName] = result.exportData;
        } catch (error) {
          console.warn(`Failed to export config ${configName}:`, error);
        }
      }

      const databaseExport = {
        database: 'sharepoint-sync-v9',
        version: '1.0',
        exportMetadata: {
          exportedAt: new Date().toISOString(),
          collectionCount: targetCollections.length,
          configCount: configsToExport.length,
          totalRecords,
          airliftVersion: '2.0.0',
        },
        collections: exportedCollections,
        configs: exportedConfigs,
      };

      // Download file if filename provided
      if (filename) {
        downloadJsonFile(databaseExport, filename);
      }

      const result = {
        success: true,
        collectionCount: targetCollections.length,
        configCount: configsToExport.length,
        totalRecords,
        exportData: filename ? null : databaseExport,
      };

      lastOperationResult.value = result;
      return result;
    } catch (error) {
      const result = {
        success: false,
        error: error.message,
        code: 'EXPORT_ERROR',
      };

      lastOperationResult.value = result;
      throw error;
    } finally {
      isExporting.value = false;
    }
  }

  /**
   * Imports entire database
   * @param {object} data - Database import data
   * @param {object} options - Import options
   * @param {string} options.strategy - Default strategy for all collections
   * @returns {Promise<object>} Import results for each collection
   */
  async function importDatabase(data, options = {}) {
    const { strategy = 'upsert' } = options;

    isImporting.value = true;

    try {
      // Validate format
      validateDatabaseExportFormat(data);

      const results = { collections: {}, configs: {} };

      for (const [name, collectionData] of Object.entries(
        data.collections || {}
      )) {
        try {
          results.collections[name] = await importCollection(
            name,
            collectionData,
            { strategy }
          );
        } catch (error) {
          results.collections[name] = { success: false, error: error.message };
        }
      }

      for (const [name, configData] of Object.entries(data.configs || {})) {
        if (name === '_user') continue;
        try {
          results.configs[name] = await importConfig(name, configData);
        } catch (error) {
          results.configs[name] = { success: false, error: error.message };
        }
      }

      lastOperationResult.value = results;
      return results;
    } catch (error) {
      const result = {
        success: false,
        error: error.message,
        code: 'VALIDATION_ERROR',
      };

      lastOperationResult.value = result;
      throw error;
    } finally {
      isImporting.value = false;
    }
  }

  /**
   * Determines the type of import file (single collection or database)
   * @param {object} jsonData - Parsed JSON data
   * @returns {string} 'single' or 'database'
   */
  function detectFileType(jsonData) {
    if (jsonData.collection) return 'collection';
    if (jsonData.config) return 'config';
    if (jsonData.database) return 'database';
    throw new Error(
      'Invalid JSON format: must be collection, config, or database export'
    );
  }

  /**
   * Validates and parses a JSON file
   * @param {File} file - File object to process
   * @returns {Promise<object>} Parsed JSON data
   */
  async function processImportFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = event => {
        try {
          const data = JSON.parse(event.target.result);
          resolve(data);
        } catch (error) {
          reject(new Error(`Invalid JSON file: ${error.message}`));
        }
      };

      reader.onerror = () => reject(new Error('File read error'));
      reader.readAsText(file);
    });
  }

  /**
   * Formats file size for display
   * @param {number} bytes - File size in bytes
   * @returns {string} Formatted size string
   */
  function formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  return {
    // State
    isExporting,
    isImporting,
    lastOperationResult,

    // Methods
    exportCollection,
    importCollection,
    exportConfig,
    importConfig,
    exportDatabase,
    importDatabase,

    // Utilities
    detectFileType,
    processImportFile,
    formatFileSize,
  };
}
